import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Flex, Select, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { CompanyProfile } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import {
  legalAnnualTurnoverOptions,
  legalNumberOfEmployeeRangeOptions,
} from 'features/Onboarding/common/complianceProperties';
import isEi from 'features/Onboarding/common/isEi';
import Header from 'features/Onboarding/components/Header';
import OBBackButton from 'features/Onboarding/components/OBBackButton';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import locales from './locales';

const MINIMUM_LEGAL_SHARE_CAPITAL_AMOUNT_IN_EUROS = 1;

const legalAnnualTurnOverSchema = z.custom<
  (typeof legalAnnualTurnoverOptions)[number]['value']
>((val) =>
  Boolean(legalAnnualTurnoverOptions.find((option) => option.value === val)),
);

const legalNumberOfEmployeeRangeSchema = z.custom<
  (typeof legalNumberOfEmployeeRangeOptions)[number]['value']
>((val) =>
  Boolean(
    legalNumberOfEmployeeRangeOptions.find((option) => option.value === val),
  ),
);

const legalShareCapitalSchema = z
  .number({ invalid_type_error: locales.inputs.capital.errors.invalid })
  .min(
    MINIMUM_LEGAL_SHARE_CAPITAL_AMOUNT_IN_EUROS,
    locales.formatString(locales.inputs.capital.errors.minValue, {
      minimumAmount: formatAmount({
        forcePlusSign: false,
        value: MINIMUM_LEGAL_SHARE_CAPITAL_AMOUNT_IN_EUROS,
        withNoMinorUnitDigits: true,
      }),
    }),
  );

const getSchema = ({ isCompanyEI }: { isCompanyEI: boolean }) =>
  z.object({
    legalAnnualTurnOver: legalAnnualTurnOverSchema,
    legalNumberOfEmployeeRange: legalNumberOfEmployeeRangeSchema,
    legalShareCapital: isCompanyEI
      ? legalShareCapitalSchema.optional()
      : legalShareCapitalSchema,
  });

export type FinancialInfoPayload = z.infer<ReturnType<typeof getSchema>>;

interface FinancialInfoProps {
  companyData: Pick<
    CompanyProfile,
    | 'legalForm'
    | 'legalAnnualTurnOver'
    | 'legalNumberOfEmployeeRange'
    | 'legalShareCapital'
  >;
  isSaving: boolean;
  onContinue: (payload: FinancialInfoPayload) => void;
}

const FinancialInfo = asBentoPage<FinancialInfoProps>(
  ({ companyData, isSaving, Layout, onContinue, onPrev }) => {
    const isCompanyEI = useMemo(
      () => isEi(companyData.legalForm),
      [companyData.legalForm],
    );

    const schema = useMemo(() => getSchema({ isCompanyEI }), [isCompanyEI]);

    const {
      formState: { errors },
      handleSubmit,
      register,
    } = useForm({
      defaultValues: {
        legalAnnualTurnOver: companyData.legalAnnualTurnOver || undefined,
        legalNumberOfEmployeeRange:
          companyData.legalNumberOfEmployeeRange || undefined,
        // This field is set to 0 at creation.
        // In order to show an empty input, we need to override its value.
        // This won't override user-inputted values as we only allow > 0.
        // TODO(Carina): set this field to null in the DB instead of 0 at creation.
        legalShareCapital: companyData.legalShareCapital || undefined,
      },
      mode: 'onChange',
      resolver: zodResolver(schema),
    });

    const onSubmit = handleSubmit((values) => {
      onContinue(schema.parse(values));
    });

    return (
      <Layout
        data-testid="identity-info-edit"
        header={<OBBackButton onClick={onPrev} />}
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header title={locales.title} />

          <form data-testid="financial-info-edit" onSubmit={onSubmit}>
            <VStack alignItems="stretch" spacing="space-24">
              {!isCompanyEI ? (
                <Form.Field
                  error={errors.legalShareCapital?.message}
                  label={locales.inputs.capital.label}
                >
                  <Form.Input
                    {...register('legalShareCapital', { valueAsNumber: true })}
                    inputMode="numeric"
                    isDisabled={isSaving}
                    type="number"
                  />
                </Form.Field>
              ) : null}

              <Form.Field
                error={errors.legalNumberOfEmployeeRange?.message}
                label={locales.inputs.employees.label}
              >
                <Select
                  {...register('legalNumberOfEmployeeRange')}
                  isDisabled={isSaving}
                  required
                >
                  {legalNumberOfEmployeeRangeOptions.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                    </option>
                  ))}
                </Select>
              </Form.Field>

              <Form.Field
                error={errors.legalAnnualTurnOver?.message}
                label={locales.inputs.turnover.label}
              >
                <Select
                  isDisabled={isSaving}
                  {...register('legalAnnualTurnOver')}
                  required
                >
                  {legalAnnualTurnoverOptions.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                    </option>
                  ))}
                </Select>
              </Form.Field>
            </VStack>

            <Button
              isExpanded
              isLoading={isSaving}
              marginTop="space-40"
              type="submit"
            >
              {locales.continue}
            </Button>
          </form>
        </Flex>
      </Layout>
    );
  },
);

export default FinancialInfo;
